import { CHARACTER_ACTIONS } from "@/character/actions";
import { mountDraftMotionPreview } from "@/character/character-preview";
import { defaultGridTuning, normalizeGridTuning } from "@/character/draft";
import {
  applyDungeonMonsterPowerCard,
  dungeonMonsterPowerCardCount,
  stepDungeonMonsterPowerDown,
} from "@/dungeon/dungeon-monster-power-card";
import type { DungeonShopConfig } from "@/dungeon/dungeon-shop-types";
import {
  GM_MONSTER_PLACEHOLDER_IMAGE,
  type GmMonsterTemplate,
} from "@/gm/gm-monster-catalog";
import type { HomeEconomy } from "@/home/economy";
import type { TowerFloorSpawnRow } from "@/tower/tower-floor-config";
import { TOWER_DEFAULT_SPAWN_PER_MINUTE } from "@/tower/tower-spawn-rate";

const WALK_ROW = CHARACTER_ACTIONS.find((a) => a.key === "run")?.row ?? 1;

export const DUNGEON_FLOOR_MAX_MONSTER_TYPES = 5;

export type DungeonSpawnDraftRow = TowerFloorSpawnRow & { rowId: string };

export type DungeonFloorMonsterModalDeps = {
  catalog: GmMonsterTemplate[];
  shop: DungeonShopConfig;
  getEconomy: () => HomeEconomy;
  maxForMonster: (monsterId: string) => number;
  vaultCount: (monsterId: string) => number;
  selectableMonsterIds: (excludeRowId?: string) => string[];
  onEconomyUpdate: (economy: HomeEconomy) => void;
  onApply: (row: DungeonSpawnDraftRow) => void;
  onStatus: (message: string) => void;
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function monsterById(catalog: GmMonsterTemplate[], id: string): GmMonsterTemplate | undefined {
  return catalog.find((m) => m.id === id);
}

function monsterThumb(m: GmMonsterTemplate | undefined): string {
  if (!m) return GM_MONSTER_PLACEHOLDER_IMAGE;
  return m.imageDataUrl?.startsWith("data:") ? m.imageDataUrl : GM_MONSTER_PLACEHOLDER_IMAGE;
}

export function mountDungeonFloorMonsterModal(
  container: HTMLElement,
  deps: DungeonFloorMonsterModalDeps,
): {
  openForRow: (row: DungeonSpawnDraftRow) => void;
  openNew: (rowId: string) => void;
  close: () => void;
  destroy: () => void;
} {
  let editingRowId: string | null = null;
  let draftMonsterId = "";
  let draftCount = 1;
  let draftPower = 1;
  let stopPreview: (() => void) | undefined;

  const modal = document.createElement("div");
  modal.id = "dungeon-spawn-modal";
  modal.className = "gm-monster-modal dungeon-spawn-modal";
  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `
    <button type="button" class="gm-monster-modal-backdrop" data-dungeon-spawn-close aria-label="ปิด"></button>
    <div class="gm-monster-modal-dialog dungeon-spawn-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="dungeon-spawn-modal-title">
      <header class="gm-monster-modal-head">
        <h2 class="gm-monster-modal-title" id="dungeon-spawn-modal-title">มอนสเตอร์ในด่าน</h2>
        <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" data-dungeon-spawn-close aria-label="ปิด">✕</button>
      </header>
      <div class="gm-monster-modal-body dungeon-spawn-modal-body" id="dungeon-spawn-modal-body"></div>
    </div>`;
  container.appendChild(modal);

  const stopPreviewLoop = () => {
    stopPreview?.();
    stopPreview = undefined;
  };

  const mountPreview = (monsterId: string) => {
    const host = modal.querySelector<HTMLElement>("#dungeon-spawn-preview-host");
    if (!host) return;
    stopPreviewLoop();
    host.innerHTML = "";
    const m = monsterById(deps.catalog, monsterId);
    const sheet = m?.spritesheetDataUrl;
    if (sheet?.startsWith("data:")) {
      const tuning = normalizeGridTuning(m?.gridTuning ?? defaultGridTuning());
      stopPreview = mountDraftMotionPreview(
        host,
        sheet,
        tuning,
        WALK_ROW,
        m?.name ?? "มอนสเตอร์",
        { displaySizePx: 120 },
      );
      return;
    }
    host.innerHTML = `<img class="dungeon-spawn-preview-fallback" src="${escapeHtml(monsterThumb(m))}" alt="" width="96" height="96" />`;
  };

  const renderBody = () => {
    const body = modal.querySelector<HTMLElement>("#dungeon-spawn-modal-body");
    if (!body) return;
    const m = monsterById(deps.catalog, draftMonsterId);
    const cap = deps.maxForMonster(draftMonsterId);
    const vault = deps.vaultCount(draftMonsterId);
    const ids = deps.selectableMonsterIds(editingRowId ?? undefined);
    const options = ids
      .map((id) => {
        const item = monsterById(deps.catalog, id);
        const label = item?.name ?? id;
        return `<option value="${escapeHtml(id)}" ${id === draftMonsterId ? "selected" : ""}>${escapeHtml(label)}</option>`;
      })
      .join("");

    body.innerHTML = `
      <div class="dungeon-spawn-modal-layout">
        <div class="dungeon-spawn-preview-wrap">
          <div id="dungeon-spawn-preview-host" class="dungeon-spawn-preview-host" aria-hidden="false"></div>
        </div>
        <div class="dungeon-spawn-modal-fields">
          <p class="dungeon-spawn-modal-name">${escapeHtml(m?.name ?? "—")}</p>
          <p class="hint dungeon-spawn-modal-stats">
            Lv.${m?.monsterLevel ?? "—"} · HP ${(m?.monsterBaseHp ?? 0).toLocaleString("en-US")} · ATK ${(m?.monsterBaseAttack ?? 0).toLocaleString("en-US")}
          </p>
          <label class="field">
            <span class="field-label">ประเภท</span>
            <select class="input" id="dungeon-spawn-monster-select">${options}</select>
          </label>
          <label class="field">
            <span class="field-label">จำนวนในด่าน</span>
            <input type="number" class="input" id="dungeon-spawn-count" min="1" max="${Math.max(1, cap)}" value="${draftCount}" />
            <span class="hint">คลัง ${vault.toLocaleString("en-US")} · ใส่ได้สูงสุด ${cap.toLocaleString("en-US")}</span>
          </label>
          <div class="dungeon-spawn-power-block">
            <span class="field-label">คูณพลัง</span>
            <div class="dungeon-spawn-stepper dungeon-spawn-stepper--modal">
              <button type="button" class="dungeon-spawn-stepper-btn" id="dungeon-spawn-power-minus" aria-label="ลดคูณพลัง">−</button>
              <span class="dungeon-spawn-stepper-value">×${draftPower}</span>
              <button type="button" class="dungeon-spawn-stepper-btn" id="dungeon-spawn-power-plus" aria-label="เพิ่มคูณพลัง">+</button>
            </div>
            <p class="hint">บัตรคงเหลือ ${dungeonMonsterPowerCardCount(deps.getEconomy())} · +1× ต่อใบ (ซื้อร้าน Cash 10)</p>
            <p class="hint">สูงสุด ×${Math.floor(deps.shop.cashMaxPowerMultiplier)}</p>
          </div>
        </div>
      </div>
      <div class="gm-monster-modal-foot">
        <button type="button" class="btn btn-ghost" data-dungeon-spawn-close>ยกเลิก</button>
        <button type="button" class="btn btn-primary" id="dungeon-spawn-apply">ตกลง</button>
      </div>`;

    mountPreview(draftMonsterId);

    body.querySelector<HTMLSelectElement>("#dungeon-spawn-monster-select")?.addEventListener("change", (e) => {
      const sel = e.currentTarget as HTMLSelectElement;
      draftMonsterId = sel.value;
      draftCount = Math.min(draftCount, Math.max(1, deps.maxForMonster(draftMonsterId)));
      renderBody();
    });

    body.querySelector<HTMLInputElement>("#dungeon-spawn-count")?.addEventListener("change", (e) => {
      const inp = e.currentTarget as HTMLInputElement;
      const capN = deps.maxForMonster(draftMonsterId);
      draftCount = Math.max(1, Math.min(capN, Math.floor(Number(inp.value) || 1)));
      renderBody();
    });

    body.querySelector("#dungeon-spawn-power-minus")?.addEventListener("click", () => {
      draftPower = stepDungeonMonsterPowerDown(draftPower);
      renderBody();
    });

    body.querySelector("#dungeon-spawn-power-plus")?.addEventListener("click", () => {
      const res = applyDungeonMonsterPowerCard(deps.getEconomy(), deps.shop, draftPower);
      if (!res.ok) {
        deps.onStatus(res.reason);
        return;
      }
      deps.onEconomyUpdate(res.economy);
      draftPower = res.powerMultiplier;
      deps.onStatus(`คูณพลัง ×${draftPower}`);
      renderBody();
    });

    body.querySelector("#dungeon-spawn-apply")?.addEventListener("click", () => {
      if (!editingRowId || !draftMonsterId) return;
      const capN = deps.maxForMonster(draftMonsterId);
      if (capN <= 0) {
        deps.onStatus("คลังมอนสเตอร์ไม่พอ — ซื้อที่ร้าน");
        return;
      }
      draftCount = Math.max(1, Math.min(capN, draftCount));
      deps.onApply({
        rowId: editingRowId,
        monsterId: draftMonsterId,
        count: draftCount,
        powerMultiplier: draftPower,
        spawnPerMinute: TOWER_DEFAULT_SPAWN_PER_MINUTE,
      });
      close();
    });
  };

  const open = () => {
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("gm-monster-modal-open");
    renderBody();
  };

  const close = () => {
    stopPreviewLoop();
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("gm-monster-modal-open");
    editingRowId = null;
  };

  modal.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-dungeon-spawn-close]")) {
      close();
    }
  });

  return {
    openForRow: (row: DungeonSpawnDraftRow) => {
      editingRowId = row.rowId;
      draftMonsterId = row.monsterId;
      draftCount = row.count;
      draftPower = row.powerMultiplier ?? 1;
      open();
    },
    openNew: (rowId: string) => {
      const ids = deps.selectableMonsterIds();
      const first = ids[0];
      if (!first) {
        deps.onStatus("ซื้อมอนสเตอร์ที่ร้านก่อน");
        return;
      }
      editingRowId = rowId;
      draftMonsterId = first;
      draftCount = 1;
      draftPower = 1;
      open();
    },
    close,
    destroy: () => {
      close();
      modal.remove();
    },
  };
}
